<template>
    <app-layout>
        <asset-details-header />
        <div>
            <row>
                <column lg="6">
                    <asset-stakeholders-card v-if="isTablet" />
                    <card>
                        <template #header>
                            <custom-card-header
                                :href="$route('assets.edit', asset.id)"
                                :label="trans('general.action.edit')"
                                :show-action="hasAnyPermission(['asset.update', 'asset.location.update'])"
                                :title="trans('assets.details.header')"
                                actionIcon="cil-pencil"
                            />
                        </template>

                        <dl class="m-0">
                            <div class="row my-1">
                                <dt class="col-md-5">
                                    {{ trans('assets.details.status.label') }}
                                </dt>
                                <dd class="col-md-7 text-muted">
                                    <div v-if="hasAllPermissions(['asset.update_usage_category', 'asset.update'])">
                                        <tooltip-wrapper
                                            :enabled="isOperationInProgress || !isAssetOnboarded"
                                            :tooltip="trans('asset.action_disabled_due_to_incomplete_onboarding')"
                                            class="block"
                                        >
                                            <usage-category-selection-dropdown
                                                :disabled="isOperationInProgress || !isAssetOnboarded"
                                                :is-processing="isChangingUsageCategory"
                                                :model-value="asset.status.usageCategory"
                                                :usage-categories="usageCategories"
                                                @update:model-value="updateAssetUsageCategory"
                                            />
                                        </tooltip-wrapper>
                                        <confirm-asset-usage-category-change-modal />
                                    </div>
                                    <div v-else>
                                        <b>
                                            {{ currentAssetUsageCategory.label }}
                                        </b>
                                    </div>
                                    <div
                                        v-if="
                                            hasFeature('asset-status-check') &&
                                            hasAnyPermission('asset.status_check.read') &&
                                            asset.status?.decommissionedAt
                                        "
                                    >
                                        <status-check-date :status="asset.status" :timezone="asset.timezone" class="text-small" />
                                    </div>
                                </dd>
                            </div>
                            <div v-for="(value, key) in fields" :key="key" class="row my-1">
                                <dt class="col-md-5">
                                    {{ trans(`assets.details.${snakeCase(key)}.label`) }}
                                </dt>
                                <dd class="col-md-7 text-muted">
                                    <span v-if="value && typeof value === 'object'" :class="value?.classes" class="text-pre-line">{{
                                        value.value
                                    }}</span>
                                    <span v-else class="text-pre-line">{{ value || trans('general.not_available') }}</span>
                                </dd>
                            </div>
                        </dl>
                    </card>
                    <quality-assurance-configuration-card
                        v-if="hasFeature('quality_assurance_task_scheduling')"
                        :asset-id="asset.id"
                        :disabled="asset.status?.isOnboarded !== true"
                    />
                </column>
                <column lg="6">
                    <asset-stakeholders-card v-if="!isTablet" />
                    <configuration-overview
                        v-if="hasAnyPermission('configuration.read')"
                        :asset="asset"
                        :configurations="configurations"
                        :qualifications="qualifications"
                    />
                    <card>
                        <template #header>
                            <custom-card-header
                                :href="route('assets.edit', asset.id) + '#attachments'"
                                :title="trans('attachments.overview.header')"
                            />
                        </template>

                        <attachments
                            :attachments="attachments"
                            :can-create="hasAnyPermission(['asset.attachment.create'])"
                            :can-delete="hasAnyPermission(['asset.attachment.delete'])"
                            :can-edit="hasAnyPermission(['asset.attachment.update'])"
                            :can-read="hasAnyPermission(['asset.attachment.index'])"
                            :store-route="
                                route('assets.attachments.store', {
                                    asset: asset.id,
                                })
                            "
                            @updated-attachment="reloadAttachments"
                            @deleted-attachment="reloadAttachments"
                        />
                    </card>
                </column>
            </row>
            <row>
                <column class="d-flex align-items-center justify-content-center">
                    <confirmed-delete
                        v-if="hasAnyPermission('asset.delete')"
                        ref="confirmDelete"
                        :buttonTitle="trans('assets.details.confirmation.delete_asset.title')"
                        :is-button-disabled="!deleteAssetForm.isDirty"
                        :isProcessing="isOperationInProgress"
                        @onCancel="resetForm()"
                        @onDelete="deleteAsset()"
                    >
                        <template #title>
                            {{ trans('assets.details.confirmation.delete_asset.title') }}
                        </template>
                        <template #content>
                            <nl2br :text="trans('assets.details.confirmation.delete_asset.content')" />
                            <jet-text-area
                                v-model="deleteAssetForm.reason"
                                :horizontal="false"
                                :invalidFeedback="deleteAssetForm.errors.reason"
                                :isValid="!deleteAssetForm.errors.reason"
                                :label="trans('assets.details.confirmation.delete_asset.reason.label')"
                                :placeholder="trans('assets.details.confirmation.delete_asset.reason.description')"
                                :required="true"
                                :rows="Number(5)"
                                class="mt-3"
                            />
                        </template>
                    </confirmed-delete>
                </column>
            </row>
        </div>
    </app-layout>
</template>
<script>
import AppLayout from '@/Layouts/AppLayout.vue';
import formatAsFullDate from '@/filters/fullDate';
import ConfigurationOverview from '@/Components/ConfigurationOverview.vue';
import FileList from '@/Components/FileList.vue';
import AssetDetailsHeader from '@/Pages/Assets/Details/AssetDetailsHeader.vue';
import CustomCardHeader from '@/Components/CustomCardHeader.vue';
import ConfirmedDelete from '@/Components/ConfirmedDelete.vue';
import JetTextArea from '@/Jetstream/Textarea.vue';
import StatusCheckDate from '@/Components/StatusCheckDate.vue';
import CadenceCardWithModal from '@/Components/CadenceCard.vue';
import TooltipWrapper from '@/Pages/Assets/Details/TooltipWrapper.vue';
import AdditionalStatusInformation from '@/Components/AdditionalStatusInformation.vue';
import AssetStakeholdersCard from '@/Pages/Assets/Details/AssetStakeholdersCard.vue';
import StatusBadge from '@/Components/StatusBadge.vue';
import UsageCategorySelectionDropdown from '@/Pages/Assets/Details/UsageCategorySelectionDropdown.vue';
import ConfirmAssetUsageCategoryChangeModal from '@/Pages/Assets/Details/ConfirmAssetUsageCategoryChangeModal.vue';
import { eventBus, events } from '@/eventBus';
import Card from '@/Components/Card.vue';
import { useCurieIdScanner } from '@/composables/useCurieIdScanner.ts';
import FileUpload from '@/Components/FileUpload.vue';
import Attachments from '@/Components/Attachments.vue';
import { route } from 'ziggy-js';
import { snakeCase } from '../../../utils.js';
import QualityAssuranceConfigurationCard from './QualityAssuranceConfiguration/QualityAssuranceConfigurationCard.vue';
import { isTablet } from '@/Utils/UseMedia.js';

const { openForAttachingToAsset } = useCurieIdScanner();

export default {
    components: {
        Attachments,
        FileUpload,
        Card,
        UsageCategorySelectionDropdown,
        ConfirmAssetUsageCategoryChangeModal,
        StatusBadge,
        AssetStakeholdersCard,
        AdditionalStatusInformation,
        TooltipWrapper,
        CadenceCardWithModal,
        AppLayout,
        ConfigurationOverview,
        FileList,
        AssetDetailsHeader,
        CustomCardHeader,
        ConfirmedDelete,
        JetTextArea,
        StatusCheckDate,
        QualityAssuranceConfigurationCard,
    },

    data() {
        return {
            changes: null,
            showActivityDetails: false,
            activeTab: 0,
            isOperationInProgress: false,
            isUpdateAssetStatusInProgress: false,
            isConfirmAssetStatusChangeInProgress: false,
            deleteAssetForm: this.$inertia.form({
                reason: null,
            }),
            isChangingUsageCategory: false,
        };
    },

    computed: {
        isTablet() {
            return isTablet.value;
        },
        asset() {
            return this.$page.props.asset;
        },
        attachments() {
            return this.$page.props.attachments?.data;
        },
        configurations() {
            return this.$page.props.configurations;
        },
        qualifications() {
            return this.$page.props.qualifications.data;
        },
        currentAssetUsageCategory() {
            let usageCategory = this.usageCategories.find((el) => el.value === this.asset.status.usageCategory);
            if (this.isAssetOnboarded && usageCategory) {
                return usageCategory;
            }

            return {
                label: this.trans('assets.status.onboarded'),
                value: null,
            };
        },
        usageCategories() {
            return this.$page.props.usageCategories.data;
        },
        location() {
            return {
                location: '',
                specificLocation: '',
                area: '',
            };
        },
        assetLocalTimezone() {
            return this.asset.location.timezone.identifier;
        },
        fields() {
            const notAvailable = this.trans('general.not_available');
            return {
                serial_number: this.asset.serial_number || notAvailable,
                screening_application: this.asset.screening_application?.label || notAvailable,
                criticality: this.asset.critical ? this.trans('assets.details.criticality.yes') : this.trans('assets.details.criticality.no'),
                purchased_at: formatAsFullDate(this.asset.purchased_at, 'en'),
                deployed_at: formatAsFullDate(this.asset.deployed_at, 'en'),
                warranty: this.getWarrantyInfo(),
            };
        },
        isAssetOnboarded() {
            return !!this.asset.curieId;
        },
    },

    mounted() {
        this.startScannerIfNecessary();
    },

    methods: {
        snakeCase,
        route,
        deleteAsset() {
            this.isOperationInProgress = true;
            this.deleteAssetForm.delete(route('assets.destroy', this.asset), {
                onFinish: () => {
                    this.isOperationInProgress = false;
                },
            });
        },
        updateAssetUsageCategory(usageCategory) {
            if (usageCategory === this.asset.status.usageCategory) {
                return;
            }

            if (this.requiresUsageCategoryAttestation(usageCategory)) {
                eventBus.$emit(events.openConfirmAssetUsageCategoryChangeModal, {
                    assetId: this.asset.id,
                });
                return;
            }

            this.isOperationInProgress = true;
            this.isChangingUsageCategory = true;

            this.$inertia.post(
                this.route('assets.update-usage-category', this.asset),
                { usageCategory },
                {
                    preserveState: true,
                    onFinish: () => {
                        this.isOperationInProgress = false;
                        this.isChangingUsageCategory = false;
                    },
                },
            );
        },
        requiresUsageCategoryAttestation(usageCategory) {
            return usageCategory === 'active' && this.$page.props.assetReintegrationUsageCategoryAttestationRequired === true;
        },
        resetForm() {
            this.deleteAssetForm.reset();
            this.deleteAssetForm.clearErrors();
        },
        getWarrantyInfo() {
            let startDate = formatAsFullDate(this.asset.warranty?.start_date, this.currentLocale);
            let endDate = formatAsFullDate(this.asset.warranty?.end_date, this.currentLocale);

            if (!startDate && !endDate) {
                return this.trans('general.not_available');
            }

            if (startDate && endDate) {
                return `${startDate} - ${endDate}`;
            }

            if (startDate) {
                return this.trans('assets.details.warranty_start_date.label') + ' ' + startDate;
            }

            if (endDate) {
                return this.trans('assets.details.warranty_end_date.label') + ' ' + endDate;
            }
        },
        getLocationInfo(level = 0) {
            const location = this.asset.location.hierarchy.find((el) => el.level === level);

            if (!location) {
                return this.trans('general.not_available');
            }

            if (level === 0) {
                return `${location.iataCode} - ${location.name}`;
            }
            return location.name;
        },
        startScannerIfNecessary() {
            if (!!this.asset.curieId || !this.route().params.scan) {
                return;
            }

            openForAttachingToAsset(this.asset.id);
        },
        reloadAttachments() {
            this.$inertia.reload({ only: ['attachments'] });
        },
    },
};
</script>
<style>
.popover-old {
    z-index: 999 !important;
}

.popover-no-header + .popover-old > .popover-old-header {
    display: none;
}
</style>
